/**
 * Deep-link URLs for the admin Explore AgentThreads tab.
 *
 * Admins share these links with other admins to point them straight at a
 * thread or a specific feedback entry. All params are picked up by
 * ``ExploreAgentThreads`` on mount.
 */

import { ADMIN_EXPLORE_AGENT_THREADS_TAB_INDEX } from "@/Admin/tabIndex";
import { Env } from "@/shared/environment/Env";

export interface AdminAgentThreadLinkParams {
  sessionId: string;
}

export interface AdminFeedbackLinkParams {
  sessionId: string;
  feedbackId: string;
}

/**
 * Build a fully-qualified URL to ``/admin`` that honours the React Router
 * ``basename`` used on branch-per-URL deploys (e.g. ``/feat-Feedback/admin``).
 *
 * ``window.location.origin`` alone drops the basename, which produces a
 * whitescreen on branch deployments.
 */
function buildAdminUrl(searchParams: URLSearchParams): string {
  const url = new URL(`${Env.appBasePath}/admin`, window.location.origin);
  url.search = searchParams.toString();
  return url.toString();
}

/** URL to open a specific thread inside the admin AgentThreads view. */
export function buildAdminAgentThreadLink(
  params: AdminAgentThreadLinkParams,
): string {
  const searchParams = new URLSearchParams();
  searchParams.set("tab", String(ADMIN_EXPLORE_AGENT_THREADS_TAB_INDEX));
  searchParams.set("view", "agentThreads");
  searchParams.set("sessionId", params.sessionId);
  return buildAdminUrl(searchParams);
}

/** URL to open a specific feedback entry inside the admin Feedback view. */
export function buildAdminFeedbackLink(
  params: AdminFeedbackLinkParams,
): string {
  const searchParams = new URLSearchParams();
  searchParams.set("tab", String(ADMIN_EXPLORE_AGENT_THREADS_TAB_INDEX));
  searchParams.set("view", "feedback");
  searchParams.set("sessionId", params.sessionId);
  searchParams.set("feedbackId", params.feedbackId);
  return buildAdminUrl(searchParams);
}
